import React from 'react';
import clsx from 'clsx';
import {ArrowLeft, ArrowRight, ArrowUp, Check, Eye, ScanFace} from 'lucide-react';
import {LivenessStep, StepStatus} from './types';

export const Container = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={clsx(
      'relative flex h-full w-full animate-[fade-in_0.3s_ease-in-out] flex-col items-center justify-center',
      className,
    )}
    {...props}
  />
));

Container.displayName = 'Container';

export const SpecialIndicator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={clsx(
      'absolute right-1 top-1 z-[2] h-0.5 w-0.5 rounded-full bg-white',
      className,
    )}
    {...props}
  />
));

SpecialIndicator.displayName = 'SpecialIndicator';

const TURN_STEPS: LivenessStep[] = ['TURN_LEFT', 'TURN_RIGHT', 'TURN_UP'];

const LIVENESS_CHALLENGE_COUNT = 3;

const getChallengeIndex = (step: LivenessStep) => {
  if (step === 'LOOK_STRAIGHT') return 0;
  if (TURN_STEPS.includes(step)) return 1;
  if (step === 'BLINK') return 2;
  return LIVENESS_CHALLENGE_COUNT;
};

type ChallengeProps = {
  step: LivenessStep;
  status: StepStatus;
  className?: string;
};

export const ChallengeDots = ({step, status, className}: ChallengeProps) => {
  const currentIndex = getChallengeIndex(step);
  const isCurrentPassed = status === 'success';

  return (
    <div
      aria-hidden
      className={clsx(
        'absolute bottom-[4%] left-1/2 z-[5] flex -translate-x-1/2 gap-[7px]',
        className,
      )}
    >
      {Array.from({length: LIVENESS_CHALLENGE_COUNT}, (_, index) => {
        const isDone =
          index < currentIndex || (index === currentIndex && isCurrentPassed);
        const isActive = index === currentIndex && !isCurrentPassed;

        return (
          <span
            key={index}
            className={clsx(
              'h-2 w-2 rounded-full transition-colors duration-300',
              isDone && 'bg-[var(--chekin-green-500)]',
              isActive &&
                'bg-[var(--chekin-color-brand-light)] [box-shadow:0_0_8px_var(--chekin-color-brand-light)]',
              !isDone && !isActive && 'bg-white/35',
            )}
          />
        );
      })}
    </div>
  );
};

const CUE_ICON_CLASS = 'h-[18px] w-[18px] flex-none';

const nudge = (axis: 'x' | 'y', offset: string) =>
  ({[`--liveness-nudge-${axis}`]: offset} as React.CSSProperties);

const CUE_KEYFRAMES = `
@keyframes liveness-cue-nudge {
  0%, 100% { transform: translate(0, 0); opacity: 1; }
  50% {
    transform: translate(var(--liveness-nudge-x, 0), var(--liveness-nudge-y, 0));
    opacity: 0.6;
  }
}
@keyframes liveness-cue-blink {
  0%, 40%, 100% { opacity: 1; transform: scaleY(1); }
  50% { opacity: 0.35; transform: scaleY(0.1); }
}
.liveness-cue-nudge { animation: liveness-cue-nudge 1.2s ease-in-out infinite; }
.liveness-cue-blink { animation: liveness-cue-blink 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .liveness-cue-nudge, .liveness-cue-blink { animation: none !important; }
}
`;

export const ChallengeCueStyles = () => <style>{CUE_KEYFRAMES}</style>;

export const ChallengeCueIcon = ({step, status}: ChallengeProps) => {
  if (status === 'success' || step === 'COMPLETED') {
    return (
      <Check
        className={clsx(CUE_ICON_CLASS, 'text-[var(--chekin-success)]')}
        strokeWidth={3}
      />
    );
  }

  const nudgeClass = clsx('liveness-cue-nudge', CUE_ICON_CLASS);

  switch (step) {
    case 'TURN_LEFT':
      return <ArrowLeft className={nudgeClass} style={nudge('x', '-5px')} />;
    case 'TURN_RIGHT':
      return <ArrowRight className={nudgeClass} style={nudge('x', '5px')} />;
    case 'TURN_UP':
      return <ArrowUp className={nudgeClass} style={nudge('y', '-5px')} />;
    case 'BLINK':
      return <Eye className={clsx('liveness-cue-blink', CUE_ICON_CLASS)} />;
    default:
      return <ScanFace className={CUE_ICON_CLASS} />;
  }
};
